import { useEffect, useRef } from 'react';

interface ChatMessageLike {
  role: string;
  content: string;
}

/**
 * The assistant's reply to the most recent user message reading `request`:
 * every assistant message after it, up to the next user message. `null` when
 * the request is on the thread with no reply; `undefined` when the request
 * isn't on the thread yet (still queued).
 */
export function findReply(
  messages: readonly ChatMessageLike[],
  request: string,
): string | null | undefined {
  let userIndex = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role === 'user' && message.content === request) {
      userIndex = i;
      break;
    }
  }
  if (userIndex === -1) return undefined;
  const replies: string[] = [];
  for (const message of messages.slice(userIndex + 1)) {
    if (message.role === 'user') break;
    if (message.role === 'assistant' && message.content.trim()) {
      replies.push(message.content.trim());
    }
  }
  return replies.length > 0 ? replies.join('\n\n') : null;
}

const REPLY_TIMEOUT_MS = 180_000;
const FAILED_REPLY =
  'The assistant could not complete that request. Tell the user and offer to try again.';
const TIMED_OUT_REPLY =
  'The assistant is still working on that. Tell the user the answer will appear in the chat panel.';

interface PendingReply {
  request: string;
  resolve: (reply: string) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Turns "send a chat message" into "send it and await the reply text", for a
 * voice call to speak. Resolves once the turn has finished (`isLoading` goes
 * false) and a reply to the request is on the thread.
 */
export function useAgentReplyWaiter({
  messages,
  isLoading,
  error,
}: {
  messages: readonly ChatMessageLike[];
  isLoading: boolean;
  error: string | null;
}): (request: string) => Promise<string> {
  const pendingRef = useRef<PendingReply[]>([]);

  // Syncs an external promise with chat state, which only arrives via render.
  useEffect(() => {
    if (isLoading || pendingRef.current.length === 0) return;
    const stillPending: PendingReply[] = [];
    for (const pending of pendingRef.current) {
      const reply = findReply(messages, pending.request);
      // Still queued (its message not on the thread yet) — keep waiting. A
      // turn that ended with no reply (stopped, failed) settles now.
      if (reply === undefined && !error) {
        stillPending.push(pending);
        continue;
      }
      clearTimeout(pending.timer);
      pending.resolve(reply ?? FAILED_REPLY);
    }
    pendingRef.current = stillPending;
  }, [messages, isLoading, error]);

  useEffect(
    () => () => {
      for (const pending of pendingRef.current) clearTimeout(pending.timer);
      pendingRef.current = [];
    },
    [],
  );

  return (request: string) =>
    new Promise<string>((resolve) => {
      const timer = setTimeout(() => {
        pendingRef.current = pendingRef.current.filter(
          (p) => p.timer !== timer,
        );
        resolve(TIMED_OUT_REPLY);
      }, REPLY_TIMEOUT_MS);
      pendingRef.current.push({ request, resolve, timer });
    });
}
